import { computed, defineComponent, PropType } from "vue";
import { mapActions, mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";
import moment, { Moment } from "moment";
import draggable from "vuedraggable";
import "bootstrap-datepicker";

import config from "../../../../common/config";
import { NavigationService } from "../../../../services/navigation.service";
import { AnalyticsCategory, AnalyticsService } from "../../../../services/analytics.service";
import { TaskService } from "../../../../services/task.service";
import type { ILogOptions } from "../../../../resources-vue/vue-interfaces/i-analytics";
import type { INavigateRequestParams, INavigateResponse } from "../../../../resources-vue/vue-interfaces/i-navigation";
import type { ITask, ITaskReorderRequestBody } from "../../../../resources-vue/vue-interfaces/i-task";
import { formatDate, formatPhone } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { ApplicationEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../../enums/enums";
import { getApplicationId } from "../../../../common/vue-helpers/modifiers/application-modifiers";
import { ToastrService } from "../../../../services/toastr.service";
import { useLoadingIndicator } from "../../../Common/LoadingIndicator/store/useLoadingIndicator";
import type { ILocation } from "./../../../../resources-vue/vue-interfaces/i-location";
import eventBus from "../../../../utilities/eventBus";
import { GeoLocationEvents } from "../../../../services-vue/geolocation.service";
import { GoogleMapsService } from "./../../../../services/google-maps.service";
import { useApp } from "../../../App/useApp";
import { getHospiceRedirectionUrl } from "../../../../common/get-hospice-redirection-url";

import CardTemplate from "../../../../resources-vue/vue-elements/CardTemplate/CardTemplate.vue";
import ProductPill from "../../../../resources-vue/vue-elements/ProductPill/ProductPill.vue";
import Addendum from "../../../../resources-vue/vue-dialogs/AddAddendum/AddAddendum.vue";
import TasksMap from "../TasksMap/TasksMap.vue";
import VisitDetails from "../../../../resources-vue/vue-dialogs/VisitDetails/VisitDetails.vue";

export default defineComponent({
    components: {
        CardTemplate,
        ProductPill,
        TasksMap,
        draggable
    },

    data() {
        return {
            myTasksDate: {} as Moment,
            taskList: [] as ITask[],
            selectedTask: {} as ITask,
            listUpdate: 0,
            _permissionManager: null,
            _navigateService: null,
            _analyticsService: null,
            _googleMapsService: null,
            _toastrService: null,
            _taskService: null,
            _dpDateFormat: "MM-DD-YYYY",
            _dpStartDate: moment().subtract(60, "days").format("MM-DD-YYYY") as string,
            _dpEndDate: moment().add(14, "days").format("MM-DD-YYYY") as string,
            AxxessPlannerVisits: "Axxess Planner Visits",
            _visits: "visits",
            _noVisits: "noVisits",
            isReopenAllowed: false,
            taskCountByDateList: [] as Array<any>,
            noVisitDatePicker: null,
            datepicker: null,
            container: null,
            decrementDisabled: false,
            incrementDisabled: false,
            showCalendar: false,
            isLoading: true,
            notesLimit: 60,
            hasError: false,
            taskListString: "",
            tasksContainerRef: null,
            scroll: null as any,
            dialogRef: null,
            ApplicationEnum: ApplicationEnum,
            _locationData: [] as ILocation,
            zipNotEntered: false,
            tooltip: `<p>This section displays all visits for the chosen date.</p>
                            <ul class="list-style-disc">
                                <li>Visit List
                                    <ul class="list-style-circle">
                                        <li>Click 'START' to open and document the visit note.</li>
                                        <li>Click 'Patient Chart' to view more details.</li>
                                    </ul>
                                </li>
                                <li> Visit Map
                                    <ul class="list-style-circle">
                                    <li><i class="fas fa-map-marker-alt user-location"></i> - Current Location</li>
                                    <li><i class="fas fa-map-marker-alt task-location"></i> - Visits' Location</li>
                                    <li>Click on a marker to view visit details.</li>
                                    <li>Once selected, click on 'Get Directions' to view routes to selected location.</li>
                                    </ul>
                                </li>
                            </ul>`,
            userOs: "",
            formatDate,
            formatPhone,
            getApplicationId,
            dragging: false,
            canViewTaskNotesHos: false as boolean,
            canViewTaskNotesPal: false as boolean
        };
    },

    computed: {
        ...mapState(useApp, ["isAnyPremiumAgency"]),
        isPresent: function (): boolean {
            let today = moment().format("YYYYMMDD");
            let tasksDate = moment(this.myTasksDate).format("YYYYMMDD");
            return today === tasksDate;
        },

        fwdNavTooltip: function (): string {
            let nextDate = moment(this.myTasksDate).add(1, "days").format("MMMM DD, YYYY");
            return `Click to view visits on ${nextDate}.`;
        },

        bwdNavTooltip: function (): string {
            let prevDate = moment(this.myTasksDate).subtract(1, "days").format("MMMM DD, YYYY");
            return `Click to view visits on ${prevDate}.`;
        },
        routeVisitTooltip(): string {
            return `<p>Route Visits:</p>
                    <ul>
                        <li>Drag and drop visits in the desired completion order</li>
                        <li>Select Route Visits to map all of today's visits in the selected order</li>
                    </ul>`;
        },
        isTaskList(): boolean {
            return this.taskList && this.taskList.length > 0;
        }
    },

    async created() {
        this._taskService = Container.instance.get(TaskService);
        this._navigateService = Container.instance.get(NavigationService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._toastrService = Container.instance.get(ToastrService);
        this._googleMapsService = Container.instance.get(GoogleMapsService);
        this.initSubscriptions();
        this.userOs = window.sessionStorage.getItem("userOs");
    },

    async mounted() {
        this.canViewTaskNotesHos = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.taskNotes,
            PermissionActionEnum.canView,
            ApplicationEnum.AxxessHospiceFE
        );
        this.canViewTaskNotesPal = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.taskNotes,
            PermissionActionEnum.canView,
            ApplicationEnum.AxxessPalliative
        );
        await this.taskDatePicker(this.$refs.noVisitDatePicker as HTMLElement, this._noVisits);
        await this.taskDatePicker(this.$refs.noVisitDatePicker as HTMLElement, this._visits);
        await this.getTasks();
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        ...mapActions(useApp, { showLogo: "SHOW_LOGO" }),
        async myTasksDateChanged(newValue: Moment) {
            if (newValue) {
                await this.getTasks();
                const newValueDate = newValue.format("l");
                $(this.$refs.noVisitDatePicker).datepicker("update", newValueDate);
                await this.datePickerUpdate("", this._noVisits);
            }
        },

        checkPermission(status: string, application: ApplicationEnum, agencyId: string) {
            if (status == "Reopen Visit") {
                if (application == ApplicationEnum.AgencyCore) {
                    const isReopenAllowed = this._permissionManager.checkPermissionByProduct(
                        ParentPermissionEnum.clinician,
                        PermissionActionEnum.canReopen,
                        ApplicationEnum.AgencyCore,
                        agencyId
                    );
                    return isReopenAllowed;
                } else if (application == ApplicationEnum.HomeCare) {
                    const isReopenAllowed = this._permissionManager.checkPermissionByProduct(
                        ParentPermissionEnum.clinician,
                        PermissionActionEnum.canReopen,
                        ApplicationEnum.HomeCare,
                        agencyId
                    );
                    return isReopenAllowed;
                } else if (application == ApplicationEnum.AxxessHospiceFE) {
                    const isReopenAllowed = this._permissionManager.checkPermissionByProduct(
                        ParentPermissionEnum.clinician,
                        PermissionActionEnum.canReopen,
                        ApplicationEnum.AxxessHospiceFE,
                        agencyId
                    );
                    return isReopenAllowed;
                } else {
                    const isReopenAllowed = this._permissionManager.checkPermissionByProduct(
                        ParentPermissionEnum.clinician,
                        PermissionActionEnum.canReopen,
                        ApplicationEnum.AxxessPalliative,
                        agencyId
                    );
                    return isReopenAllowed;
                }
            } else if (status === "Add Addendum" && application == ApplicationEnum.AxxessHospiceFE) {
                const isReopenAllowed = this._permissionManager.checkPermissionByProduct(
                    ParentPermissionEnum.documentAddendum,
                    PermissionActionEnum.canView,
                    ApplicationEnum.AxxessHospiceFE,
                    agencyId
                );
                return isReopenAllowed;
            } else if (status === "Add Addendum" && application == ApplicationEnum.AxxessPalliative) {
                const isReopenAllowed = this._permissionManager.checkPermissionByProduct(
                    ParentPermissionEnum.documentAddendum,
                    PermissionActionEnum.canView,
                    ApplicationEnum.AxxessPalliative,
                    agencyId
                );
                return isReopenAllowed;
            }
            return true;
        },

        selectedTaskChanged(task: ITask) {
            Object.assign(this.selectedTask, task);
        },
        updateTaskList(updatedTaskList: ITask[]) {
            this.taskList = updatedTaskList;
            this.listUpdate++;
        },
        // Date Picker for the Visits and NoVisits Section
        // Param (datePicker): datepicker or noVisitDatePicker
        // Param (isVisitsExist): visits (dropdown datepicker) or noVisits(inline datepicker)
        async taskDatePicker(datepicker: HTMLElement, isVisitsExist: string) {
            let myTasksDate: Moment = this.myTasksDate;

            $(datepicker)
                .datepicker({
                    keyboardNavigation: false,
                    startDate: this._dpStartDate, // 60 days before today
                    endDate: this._dpEndDate, // 2 weeks from today
                    autoclose: true,
                    todayBtn: "linked",
                    maxViewMode: 0
                })
                .datepicker("setDate", moment(myTasksDate).format(this._dpDateFormat))
                .off("changeDate")
                .on("changeDate", (e: DatepickerEventObject) => {
                    this.changeDate(e.date);
                    e.preventDefault();
                    e.stopPropagation();
                })
                .on("changeMonth", async (e: DatepickerEventObject) => {
                    await this.datePickerUpdate(e.date, isVisitsExist);
                });

            if (isVisitsExist === this._noVisits) {
                await this.datePickerUpdate("", isVisitsExist, true);
                let $dayHeader: JQuery<HTMLElement> = $(".datepicker-inline .datepicker-days thead>tr:nth-child(2)");
                $dayHeader.find("th:nth-child(3)").attr("colspan", "3");
            }
        },

        async getTasks() {
            try {
                const taskDate: string = moment(this.myTasksDate).format("MM/DD/YYYY");
                this.hasError = false;
                this.isLoading = true;
                this.taskList = await this._taskService.findAll({
                    date: taskDate
                });
                if (this.taskList?.length > 0) {
                    eventBus.emit("hasPlannertaskList", true);
                }
                this.indexOrder(this.taskList);
            } catch (e) {
                console.error(e);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        async indexOrder(taskList: ITask[]) {
            const tasks = taskList?.map((item: any, index: number) => {
                item.key = (index + 1).toString();
                return item;
            });
            return tasks;
        },

        changeDate(selectedDate: Date | string) {
            this.myTasksDate = moment(selectedDate);
            this.taskDateNavigation();
            this.myTasksDateChanged(this.myTasksDate);

            this._analyticsService.logEvent({
                category: AnalyticsCategory.AxxessPlannerVisits,
                action: "Planner Date picker"
            });
        },

        // Update the datepicker
        // Param(monthSelected): month selected in the datepicker
        // Param(isVisitsExist): visits(dropdowndatepicker) or noVisits(inlinedatepicker)
        async datePickerUpdate(monthSelected: Date | string, isVisitsExist: string, getUpdatedList?: boolean) {
            let myTasksDate = moment(this.myTasksDate).format(this._dpDateFormat);
            let month: Date | string = monthSelected || myTasksDate;
            // If the user has visits, then it's the datepicker-dropdown on the header
            let datePickerOrient: string =
                isVisitsExist === this._visits ? ".datepicker-orient-bottom" : ".datepicker-inline";
            let monthFormat = moment(month, "MM-DD-YYYY").format("l");
            let dateRangeOptions = this.dateRange(monthFormat);
            try {
                if (getUpdatedList) {
                    this.taskCountByDateList = await this._taskService.getUpdatedTaskDates(dateRangeOptions);
                } else {
                    this.taskCountByDateList = await this._taskService.getTaskByDates(dateRangeOptions);
                }
                if (this.taskCountByDateList?.length > 0) {
                    this.markTaskCountsInDatePicker(this.taskCountByDateList, datePickerOrient);
                }
            } catch (e) {
                console.error(e);
            }
        },

        dateRange(month: string): { startDate: string; endDate: string } {
            // let date = new Date(month);
            let firstDay = moment(month, "MM-DD-YYYY").startOf("month").format("L");
            let lastDay = moment(month, "MM-DD-YYYY").endOf("month").format("L");

            return {
                startDate: firstDay,
                endDate: lastDay
            };
        },

        markTaskCountsInDatePicker(tasksCountByDate: Array<any>, datePickerOrient: string): void {
            $('[role="tooltip"]').remove();
            let self = this;
            let activeDates: Array<string> = [];
            let count: Array<number> = [];

            if (tasksCountByDate) {
                tasksCountByDate.forEach((task) => {
                    let dateFormat = moment(task.Date, "MM/DD/YYYY");
                    activeDates.push(dateFormat.format("D"));
                    count.push(task.Count);
                });
            }

            $(datePickerOrient + " .datepicker-days tbody tr td.day").each(function () {
                let dateInCalendarElement = $(this);
                let selectedDateText = dateInCalendarElement.text();
                if ($.inArray(selectedDateText, activeDates) > -1) {
                    if (!dateInCalendarElement.hasClass("old") && !dateInCalendarElement.hasClass("new")) {
                        let dateIndex = activeDates.indexOf(selectedDateText);
                        let visitsCount = count[dateIndex];
                        let visitSingular = visitsCount > 1 ? "visits" : "visit";
                        dateInCalendarElement
                            .addClass("visits-exist")
                            .wrapInner(`<span class="visit-count visit-date"></span>`)
                            .append(`<span class="visit-count-circle"></span>`)
                            .wrapInner(
                                `<span class="tooltip-wrapper" data-bs-toggle="tooltip"
                                        title="You have ${visitsCount} ${visitSingular}."
                                        data-bs-placement="right"></span>`
                            )
                            .on("click", function (e) {
                                let date = $(this).find(".visit-date").text(); // Example: 1
                                let monthAndYear = $(datePickerOrient + " .datepicker-switch").html(); // Example: June 2017
                                let dateSelected = date + " " + monthAndYear; // 1 June 2017
                                self.changeDate(dateSelected);
                                if (datePickerOrient === ".datepicker-orient-bottom") {
                                    $(datePickerOrient).hide();
                                }
                                e.preventDefault();
                                e.stopPropagation();
                            });
                    }
                }
            });

            $('[data-bs-toggle="tooltip"]').tooltip();
        },

        isVisitCompleted(status: string) {
            const isCompleted: number = status.toLowerCase().indexOf("completed");
            if (isCompleted >= 0) {
                return true;
            }
            return false;
        },

        checkStatusName(statusName: string, status: string): boolean {
            if (status === "410" && !this.isFuture) {
                return false;
            } else if (statusName) {
                return true;
            }
            return false;
        },

        toggleVisitCard(task: ITask): void {
            let action: string = "Visit-Card-Close";

            this.hideOtherTasks(task);
            task.showDetails = !task.showDetails;

            if (task.showDetails) {
                action = "Visit-Card-Open";
            }

            this._analyticsService.logEvent({
                category: this.AxxessPlannerVisits,
                action: action
            });

            if (task.showDetails) {
                this.selectedTask = task;
            } else {
                this.selectedTask = null;
            }
        },

        hideOtherTasks(task: ITask): void {
            this.taskList?.forEach((item: any) => {
                if (task.Id !== item.Id) {
                    item.showDetails = false;
                }
            });
        },

        decrementTaskDate(): void {
            if (!this.isLoading) {
                this.myTasksDate = moment(this.myTasksDate).subtract(1, "days");
                this._analyticsService.logEvent({
                    category: this.AxxessPlannerVisits,
                    action: "Planner Date Backward"
                });
            }

            this.taskDateNavigation();
            this.myTasksDateChanged(this.myTasksDate);
        },

        incrementTaskDate(): void {
            if (!this.isLoading) {
                this.myTasksDate = moment(this.myTasksDate).add(1, "days");
                this._analyticsService.logEvent({
                    category: this.AxxessPlannerVisits,
                    action: "Planner Date Forward"
                });
            }

            this.taskDateNavigation();
            this.myTasksDateChanged(this.myTasksDate);
        },

        taskDateNavigation(): void {
            let taskDate = moment(this.myTasksDate, this._dpDateFormat);
            let incrementedDate = moment(this._dpEndDate, this._dpDateFormat);
            let decrementDate = moment(this._dpStartDate, this._dpDateFormat);

            if (taskDate >= incrementedDate) {
                this.incrementDisabled = true;
            } else {
                this.incrementDisabled = false;
            }

            if (taskDate <= decrementDate) {
                this.decrementDisabled = true;
            } else {
                this.decrementDisabled = false;
            }
        },

        async startTask(task: ITask): Promise<void> {
            try {
                let analyticsOptions: ILogOptions = {
                    category: this.AxxessPlannerVisits,
                    action: "Planner Start visit"
                };
                let navParams: INavigateRequestParams = {
                    applicationId: task.Application,
                    agencyId: task.AgencyId,
                    userId: task.UserId,
                    isClinician: true,
                    patientId: task.PatientId,
                    episodeId: task.EpisodeId,
                    taskId: task.Id,
                    locationId: task.LocationId,
                    status: task.PatientStatus ? Number(task.PatientStatus) : null,
                    payerId: task.PayerId
                };
                this.showLoading(true);
                let response: INavigateResponse = await this._navigateService.navigateToTask(navParams);
                this._navigateService.redirect(response, analyticsOptions);
            } catch (e) {
                console.error(e);
                this.showLoading(false);
            }
        },

        async viewPatient(task: ITask): Promise<void> {
            let analyticsOptions: ILogOptions = {
                category: this.AxxessPlannerVisits,
                action: "Planner View patient chart"
            };
            let navParams: INavigateRequestParams = {
                applicationId: task.Application,
                agencyId: task.AgencyId,
                userId: task.UserId,
                isClinician: true,
                patientId: task.PatientId
            };
            if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                window.location.href = `${getHospiceRedirectionUrl(task.AgencyId)}/patients/patient-chart/${
                    task.PatientId
                }?accountId=${task.AgencyId}`;
            } else {
                try {
                    this.showLoading(true);
                    let response: INavigateResponse = await this._navigateService.navigateToPatient(navParams);
                    this._navigateService.redirect(response, analyticsOptions);
                } catch (e) {
                    console.error(e);
                    this.showLoading(false);
                }
            }
        },

        formatVisitTime(startTime: Date, endTime: Date) {
            const startDateTime = moment(startTime);
            const endDateTime = moment(endTime);
            if (startTime && endTime && !startDateTime.isSame(endTime)) {
                const startHour = startDateTime.hour();
                const startMinute = startDateTime.minute();
                const endHour = endDateTime.hour();
                const endMinute = endDateTime.minute();
                if (startHour === 0 && startMinute === 0 && endHour === 0 && endMinute <= 1) {
                    return "";
                }
                if (startDateTime.isBefore(endTime, "day")) {
                    if (startDateTime.isSame(this.myTasksDate, "day")) {
                        return `${startDateTime.format("hh:mm A")} - ${endDateTime.format("MM/DD/YYYY hh:mm A")}`;
                    } else return `${startDateTime.format("MM/DD/YYYY hh:mm A")} - ${endDateTime.format(" hh:mm A")}`;
                } else {
                    return `${startDateTime.format("hh:mm A")} - ${endDateTime.format("hh:mm A")}`;
                }
            } else return "";
        },

        toggleCalendar(): void {
            this.showCalendar = !this.showCalendar;
        },

        async orderChanged(changedOrder: ITask[]) {
            const response = await this.indexOrder(changedOrder);
            this.updateTaskList(response);
            try {
                const newOrder: ITaskReorderRequestBody[] = changedOrder.map((item: ITask, index: number) => ({
                    agencyId: item.AgencyId,
                    application: item.Application,
                    taskId: item.Id,
                    order: index
                }));
                await this._taskService.changeTaskOrder(newOrder);
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Reordering Failed`,
                    message: `There was a problem while reordering your visits. Please try again.`
                });
                await this.getTasks();
            } finally {
                if (this.isLoading) this.isLoading = false;
            }
        },

        isFutureDocumentationPreventedHC(task: ITask): boolean {
            if (this.isFuture()) {
                if (task.Application === ApplicationEnum.HomeCare) {
                    return task.IsFutureDocumentationPrevented ? false : true;
                }
                return true;
            } else {
                return true;
            }
        },

        isHomeCare(task: ITask) {
            if (task.Application == ApplicationEnum.HomeCare) {
                return true;
            }
            return false;
        },

        isRed(task: ITask) {
            if (
                ((task.Application == ApplicationEnum.AgencyCore ||
                    task.Application == ApplicationEnum.HomeCare ||
                    task.Application == ApplicationEnum.AxxessHospiceFE ||
                    task.Application == ApplicationEnum.AxxessPalliative) &&
                    (task.MissedVisitPill?.MissedVisitComments || task.MissedVisitPill?.MissedVisitReason)) ||
                (task.Application == ApplicationEnum.AgencyCore && task.ReturnComments)
            ) {
                return true;
            }
            return false;
        },

        isOrange(task: ITask) {
            if (
                (task.Application == ApplicationEnum.HomeCare ||
                    task.Application == ApplicationEnum.AxxessHospiceFE ||
                    task.Application == ApplicationEnum.AxxessPalliative) &&
                task.ReturnComments?.length
            ) {
                return true;
            }
            return false;
        },

        redToolTip(task: ITask) {
            let toolNote = "";
            if (task.MissedVisitPill?.MissedVisitReason) {
                toolNote += `<span><strong>Missed Visit Reason:</strong> ${task.MissedVisitPill?.MissedVisitReason}</span>`;
            }
            if (task.MissedVisitPill?.MissedVisitComments) {
                toolNote += `${toolNote.length > 0 ? "</br>" : ""}<span><strong>Missed Visit Comments:</strong> ${task.MissedVisitPill?.MissedVisitComments}</span>`;
            }
            if (task.Application == ApplicationEnum.AgencyCore && task.ReturnComments) {
                toolNote += `${toolNote.length > 0 ? "</br></br>" : ""}<span><strong>Return Reason:</strong> ${task.ReturnComments}</span>`;
            }
            return toolNote;
        },

        isMissedVisit(status: string): boolean {
            return ["400", "401", "402", "403"].includes(status);
        },

        isFuture(): boolean {
            return moment(this.myTasksDate).isAfter(moment());
        },

        showFutureVisit(isFutureDocumentationPrevented: boolean): boolean {
            return isFutureDocumentationPrevented ? !this.isFuture() : true;
        },
        async startHosPalTask(task: ITask) {
            if (task.StatusName == "Completed") {
                const data = {
                    taskDetails: JSON.parse(JSON.stringify(task))
                };

                this.dialogRef = this.$dialog.open(Addendum, {
                    props: {
                        modal: true,
                        showHeader: false
                    },
                    data: {
                        data
                    },
                    onClose: (response: any) => {
                        if (response.data === "success") {
                            if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                                window.open(
                                    `${getHospiceRedirectionUrl(task.AgencyId)}/note/${task.Id}?accountId=${task.AgencyId}`
                                );
                            } else {
                                window.open(`${config.palliativeUrl}/note/${task.Id}`);
                            }
                        }
                    }
                });
            } else if (task.StatusName == "Pending Addendum") {
                if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                    window.open(
                        `${getHospiceRedirectionUrl(task.AgencyId)}/note/${task.Id}?accountId=${task.AgencyId}`
                    );
                } else {
                    window.open(`${config.palliativeUrl}/note/${task.Id}`);
                }
            } else {
                if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                    window.open(
                        `${getHospiceRedirectionUrl(task.AgencyId)}/note/${task.Id}?accountId=${task.AgencyId}`
                    );
                } else {
                    try {
                        const analyticsOptions: ILogOptions = {
                            category: this.AxxessPlannerVisits,
                            action: "Planner Start visit"
                        };
                        const navParams: INavigateRequestParams = {
                            applicationId: task.Application,
                            agencyId: task.AgencyId,
                            userId: task.UserId,
                            isClinician: true,
                            patientId: task.PatientId,
                            episodeId: task.EpisodeId,
                            taskId: task.Id,
                            locationId: task.LocationId,
                            status: task.PatientStatus ? Number(task.PatientStatus) : null,
                            payerId: task.PayerId
                        };
                        this.showLoading(true);
                        const response: INavigateResponse = await this._navigateService.navigateToTask(navParams);
                        this._navigateService.redirect(response, analyticsOptions);
                    } catch (e) {
                        console.error(e);
                        this.showLoading(false);
                    }
                }
            }
        },
        isHosOrPal(application: ApplicationEnum) {
            if (application == ApplicationEnum.AxxessHospiceFE || application == ApplicationEnum.AxxessPalliative) {
                return true;
            }
            return false;
        },

        routeVisits() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralVisitActions,
                action: "Planner Route visits"
            });
            const arrangedRoutes: string[] = [];
            const selfCords: string = `'${this._locationData.lat},${this._locationData.lon}'`;
            for (let task of this.taskList) {
                let address: string = "";
                if (task.PatientAddress.AddressLatitude && task.PatientAddress.AddressLongitude) {
                    address = `'${task.PatientAddress.AddressLatitude},${task.PatientAddress.AddressLongitude}'`;
                } else {
                    address = task.fullAddress;
                }
                if (!arrangedRoutes.includes(address)) {
                    arrangedRoutes.push(address);
                }
            }
            window.open(`https://www.google.com/maps/dir/${selfCords}/${arrangedRoutes.join("/")}`, "_blank");
        },

        initSubscriptions() {
            eventBus.on(GeoLocationEvents.Retrieved, async (data: ILocation) => {
                Object.assign(this._locationData, data);
                await this._init();
            });
        },

        async _init(): Promise<void> {
            await this._googleMapsService.loadMapsApi();
        },

        openVisitDetails(task: any) {
            if (task.Application == ApplicationEnum.AxxessHospice) {
                task.Application = ApplicationEnum.AxxessHospiceFE;
            }
            task.TaskName = task.VisitName;
            task.isPlanner = true;
            this.dialogRef = this.$dialog.open(VisitDetails, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    visitDetails: task
                },
                onClose: async (response) => {
                    if (response.data === "success") {
                        $("#datepicker").datepicker("destroy");
                        this.getTasks();
                        this.datePickerUpdate("", this._noVisits, true);
                    }
                }
            });
        },

        showComments(task: ITask) {
            if (!task.Comments?.length) {
                return false;
            }
            if (task.Application === ApplicationEnum.AxxessHospiceFE) {
                return this.canViewTaskNotesHos;
            } else if (task.Application === ApplicationEnum.AxxessPalliative) {
                return this.canViewTaskNotesPal;
            } else {
                return true;
            }
        }
    }
});
